import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Loader2, Eye, Code } from 'lucide-react';
import DOMPurify from 'dompurify';

interface BlogContentEditorProps {
  content: string;
  onChange: (content: string) => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export function BlogContentEditor({ 
  content, 
  onChange, 
  onRegenerate,
  isRegenerating = false 
}: BlogContentEditorProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');

  // Sanitize HTML for preview
  const sanitizedContent = DOMPurify.sanitize(content, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Content</CardTitle>
          <CardDescription>Edit the HTML content of your blog post</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {onRegenerate && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Regenerate
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
          <TabsList className="mb-4">
            <TabsTrigger value="edit" className="gap-2">
              <Code className="h-4 w-4" />
              HTML
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="edit" className="mt-0">
            <Textarea
              value={content}
              onChange={(e) => onChange(e.target.value)}
              rows={25}
              className="font-mono text-sm"
              placeholder="Enter HTML content..."
            />
          </TabsContent>
          
          <TabsContent value="preview" className="mt-0">
            <div 
              className="prose prose-sm max-w-none min-h-[400px] p-4 border rounded-md bg-background overflow-auto"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
